import { useEffect, useRef, useState } from 'react';
import { RequestErrorMessages, useClient } from '../../hooks/useClient';
import { ERROR_CODES } from '../../config/errorCodes';
import { useForm } from '../../hooks/useForm';
import { useFeaturesToggle } from '../../hooks/useFeaturesToggle';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { surveysBasePath, useSurveyForm } from '../../hooks/useSurveys';
import { ALL_FEATURES } from '../../config/features';
import { SurveyFormView, SurveyLaunch, SurveyQuestion, SurveyQuestionSlot } from '../../types/survey';
import { addTextQuestion, getSurveyProblems, isQuestionEnabled, removeTextQuestion, setRatingEnabled } from './surveyUtils';

interface SurveyEditorValues {
  questions: SurveyQuestion[];
}

const EMPTY_VALUES: SurveyEditorValues = { questions: [] };

const TRANSLATE_ERROR_MESSAGES: RequestErrorMessages = {
  summary: 'Error al traducir',
  defaultDetail: 'No se ha podido traducir la pregunta',
  [ERROR_CODES.SURVEY_TRANSLATION_UNAVAILABLE]: 'La traducción automática no está disponible ahora.',
  [ERROR_CODES.SURVEY_TRANSLATION_FAILED]: 'No se ha podido traducir. Inténtalo de nuevo.',
};

function toEditorValues(view: SurveyFormView): SurveyEditorValues {
  return {
    questions: view.questions.map((question) => ({
      ...question,
      enabled: isQuestionEnabled(question),
      texts: { ...question.texts },
    })),
  };
}

export interface SurveyEditor {
  isLoaded: boolean;
  canWrite: boolean;
  locales: string[];
  questions: SurveyQuestion[];
  activeLaunch: SurveyLaunch | null;
  hasChanged: boolean;
  isSaving: boolean;
  /** Problems of the saved survey that stop a round from starting. */
  savedProblems: string[];
  /** Problems of the survey on screen. */
  problems: string[];
  setQuestionText: (slot: SurveyQuestionSlot, locale: string, text: string) => void;
  setQuestionRequired: (slot: SurveyQuestionSlot, required: boolean) => void;
  addQuestion: () => void;
  removeQuestion: (slot: SurveyQuestionSlot) => void;
  setRating: (enabled: boolean) => void;
  /** D-A16: translates the main-language text of a question into `locale` (Google Translate, through the backend). */
  translateQuestion: (slot: SurveyQuestionSlot, locale: string) => Promise<void>;
  /** `${slot}-${locale}` of the translation in progress, if any. */
  translating: string | null;
  save: () => Promise<void>;
  discard: () => void;
}

/** Editor state lives in the page, so the round controls know about unsaved changes and switching tabs keeps edits. */
export function useSurveyEditor(): SurveyEditor {
  const { put, post } = useClient();
  const [translating, setTranslating] = useState<string | null>(null);
  const { organization } = useOrganizationContext();
  const { data: formView, mutate: mutateForm } = useSurveyForm();
  const canWrite = useFeaturesToggle([ALL_FEATURES.SURVEYS_WRITE]);
  const [baseline, setBaseline] = useState<SurveyEditorValues>();
  const { form, setForm, hasChanged, isSaving, setIsSaving, resetForm } = useForm<SurveyEditorValues>(baseline ?? EMPTY_VALUES, false);
  const hasChangedRef = useRef(hasChanged);
  hasChangedRef.current = hasChanged;

  // The 15 s refresh must never overwrite edits on screen.
  useEffect(() => {
    if (!formView) return;
    if (!hasChangedRef.current) setBaseline(toEditorValues(formView));
  }, [formView]);

  const locales = organization?.locales ?? [];

  const updateQuestions = (change: (questions: SurveyQuestion[]) => SurveyQuestion[]): void => {
    if (!canWrite) return;
    setForm((previous) => ({ questions: change(previous.questions) }));
  };

  const updateQuestion = (slot: SurveyQuestionSlot, change: (question: SurveyQuestion) => SurveyQuestion): void =>
    updateQuestions((questions) => questions.map((question) => (question.slot === slot ? change(question) : question)));

  const translateQuestion = async (slot: SurveyQuestionSlot, locale: string): Promise<void> => {
    const mainLocale = locales[0];
    const text = form.questions.find((question) => question.slot === slot)?.texts[mainLocale]?.trim();
    if (!organization || !canWrite || !mainLocale || !text) return;
    setTranslating(`${slot}-${locale}`);
    const result = await post<{ text: string }>(`${surveysBasePath(organization.id)}/translate-question`, {
      body: { text, from: mainLocale, to: locale },
      errorMessages: TRANSLATE_ERROR_MESSAGES,
    });
    if (result?.text) {
      updateQuestion(slot, (question) => ({ ...question, texts: { ...question.texts, [locale]: result.text } }));
    }
    setTranslating(null);
  };

  const save = async (): Promise<void> => {
    if (!organization || !canWrite) return;
    setIsSaving(true);
    const savedView = await put<SurveyFormView>(`${surveysBasePath(organization.id)}/form`, {
      body: { questions: form.questions },
      errorMessages: {
        summary: 'Error al guardar la encuesta',
        defaultDetail: 'No se ha podido guardar la encuesta',
      },
      successMessage: {
        summary: 'Encuesta guardada',
        detail: 'Los cambios se usarán en la próxima ronda',
      },
    });
    if (savedView) {
      setBaseline(toEditorValues(savedView));
      await mutateForm();
    }
    setIsSaving(false);
  };

  return {
    isLoaded: !!formView,
    canWrite,
    locales,
    questions: form.questions,
    activeLaunch: formView?.activeLaunch ?? null,
    hasChanged,
    isSaving,
    savedProblems: baseline ? getSurveyProblems(baseline.questions, locales) : [],
    problems: getSurveyProblems(form.questions, locales),
    setQuestionText: (slot, locale, text) =>
      updateQuestion(slot, (question) => ({ ...question, texts: { ...question.texts, [locale]: text } })),
    setQuestionRequired: (slot, required) => updateQuestion(slot, (question) => ({ ...question, required })),
    addQuestion: () => updateQuestions(addTextQuestion),
    removeQuestion: (slot) => updateQuestions((questions) => removeTextQuestion(questions, slot)),
    setRating: (enabled) => updateQuestions((questions) => setRatingEnabled(questions, enabled)),
    translateQuestion,
    translating,
    save,
    discard: resetForm,
  };
}
